import {
  CidrMask,
  isMulticastZeroIp,
  isPathLookupMaskValid,
} from '@ipfabric/utils';
import { getIpType } from '@ipfabric/utils/ipUtils';
import type { MulticastFormValues } from 'components/Graph/GraphSidePanel/GraphPathlookup/MulticastFormValues';
import type { FormikErrors } from 'formik';
import type { AppTranslations } from 'translations/translations';
import { isValidIpv4or6Address } from 'utils/formValidation/validations';
import { getIpVersion } from './helpers/getIpVersion';
import { isMulticastAddress } from './helpers/isMulticastAddress';
import { validateEntryPoints } from './helpers/validateEntryPoints';
import { validateAdvancedOptions } from './validateAdvancedOptions';

export const validateMulticast =
  (msg: AppTranslations) =>
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable for validation
  (values: MulticastFormValues): FormikErrors<MulticastFormValues> => {
    const errors: FormikErrors<MulticastFormValues> = {};

    if (!values.source || !isValidIpv4or6Address(values.source)) {
      errors.source = 'Invalid IPv4 or IPv6 address or range';
    } else if (
      !isPathLookupMaskValid(
        values.source,
        CidrMask.networkMode[getIpType(values.source)],
      ) &&
      !isMulticastZeroIp(values.source)
    ) {
      errors.source =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.shortMask(
          CidrMask.networkMode[getIpType(values.source)],
        );
    }

    if (isMulticastAddress(values.source)) {
      errors.source =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.multicastSource;
    }

    if (!values.group || !isValidIpv4or6Address(values.group)) {
      errors.group = 'Invalid IPv4 or IPv6 address or range';
    } else if (values.group === values.receiver) {
      errors.group =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.notSameAsSource;
    } else if (
      !isPathLookupMaskValid(
        values.group,
        CidrMask.networkMode[getIpType(values.group)],
      )
    ) {
      errors.group =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.shortMask(
          CidrMask.networkMode[getIpType(values.group)],
        );
    }

    if (values.receiver && !isValidIpv4or6Address(values.receiver)) {
      errors.receiver = 'Invalid IPv4 or IPv6 address or range';
    } else if (
      values.receiver &&
      !isPathLookupMaskValid(
        values.receiver,
        CidrMask.networkMode[getIpType(values.receiver)],
      ) &&
      !isMulticastZeroIp(values.receiver)
    ) {
      errors.receiver =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.shortMask(
          CidrMask.networkMode[getIpType(values.receiver)],
        );
    }

    const sourceVersion = getIpVersion(values.source);
    if (sourceVersion !== null) {
      const groupVersion = getIpVersion(values.group);

      if (groupVersion !== null && groupVersion !== sourceVersion) {
        errors.group = 'Cannot combine v4 and v6 addresses in one request';
      }

      const receiverVersion = getIpVersion(values.receiver);

      if (receiverVersion !== null && receiverVersion !== sourceVersion) {
        errors.receiver = 'Cannot combine v4 and v6 addresses in one request';
      }
    }

    const entryPointsErrors = validateEntryPoints(values, msg);
    if (entryPointsErrors) {
      errors.entryPoints = entryPointsErrors;
    }

    if (values.protocol === 'icmp' && sourceVersion !== 'ipv4') {
      errors.protocol =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.icmpv4IpTypeMismatch;
    }

    if (values.protocol === 'icmpv6' && sourceVersion !== 'ipv6') {
      errors.protocol =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.icmpv6IpTypeMismatch;
    }

    return {
      ...errors,
      ...validateAdvancedOptions(msg, values),
    };
  };
